// First Created - 26 May 2026
// Author - Atul Pandey
// Purpose - RTK Query API slice for Session QNA setup

import {
    createSelector,
    createEntityAdapter,
} from '@reduxjs/toolkit'
import { apiSlice } from '@/app/api/apiSlice'

const sessionQNAsAdapter = createEntityAdapter({})

const initialState = sessionQNAsAdapter.getInitialState()

export const sessionQNAsApiSlice = apiSlice.injectEndpoints({
    endpoints: builder => ({
        getSessionQNAs: builder.query({
            query: () => ({
                url: '/sessionQNAs/getSessionQNAs',
                validateStatus: (response, result) => {
                    return response.status === 200 && !result.isError
                }
            }),
            transformResponse: responseData => {
                const loadedSessionQNAs = responseData.map(sessionQNA => {
                    sessionQNA.id = sessionQNA._id
                    return sessionQNA
                })
                return sessionQNAsAdapter.setAll(initialState, loadedSessionQNAs)
            },
            providesTags: (result, error, arg) => {
                if (result?.ids) {
                    return [
                        { type: 'SessionQNA', id: 'LIST' },
                        ...result.ids.map(id => ({ type: 'SessionQNA', id }))
                    ]
                } else {
                    return [{ type: 'SessionQNA', id: 'LIST' }]
                }
            },
        }),

        createNewSessionQNA: builder.mutation({
            query: initialSessionQNA => ({
                url: '/sessionQNAs/createNewSessionQNA',
                method: 'POST',
                body: { ...initialSessionQNA }
            }),
            invalidatesTags: [
                { type: 'SessionQNA', id: 'LIST' }
            ]
        }),

        updateSessionQNA: builder.mutation({
            query: initialSessionQNA => ({
                url: '/sessionQNAs/updateSessionQNA',
                method: 'PATCH',
                body: { ...initialSessionQNA }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'SessionQNA', id: arg.id },
                { type: 'SessionQNA', id: 'LIST' }
            ],
        }),

        deleteSessionQNA: builder.mutation({
            query: ({ id, deleteMode = "soft" }) => ({
                url: '/sessionQNAs/deleteSessionQNA',
                method: 'DELETE',
                body: { id, deleteMode }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'SessionQNA', id: arg.id },
                { type: 'SessionQNA', id: 'LIST' }
            ]
        }),
    }),
})

export const {
    useGetSessionQNAsQuery,
    useCreateNewSessionQNAMutation,
    useUpdateSessionQNAMutation,
    useDeleteSessionQNAMutation,
} = sessionQNAsApiSlice

export const selectSessionQNAResult = sessionQNAsApiSlice.endpoints.getSessionQNAs.select()

const selectSessionQNAsData = createSelector(
    selectSessionQNAResult,
    sessionQNAResult => sessionQNAResult.data
)

export const {
    selectAll: selectAllSessionQNAs,
    selectById: selectSessionQNAById,
    selectIds: selectSessionQNAIds,
} = sessionQNAsAdapter.getSelectors(state => selectSessionQNAsData(state) ?? initialState)